/**
 * LeadsView.jsx - the Leads page: the lead pipeline as a Board or a Table.
 *
 * A lead IS an account (a site, or a company with no sites, whose Designation
 * is still Lead); the page is a saved view over crm_accounts, not a second
 * table. Paul's Phase 1.2 rules (L-03, L-11): only Active or Cold leads show,
 * picked with the Active | Cold toggle; Inactive, Dead and Customers never do.
 * The toggle and every filter live HERE so they hold when switching between
 * Board and Table, and both views get the very same cards
 * (components/crm/leadsBoard.js). Each user's last view and toggle are
 * remembered in this browser.
 *
 * Clicking a card or row opens the same AccountDetailPanel the Accounts tab
 * uses, rendered here rather than routed, so the page keeps its filters
 * behind it. #/leads?account=<id> opens a record directly (Dashboard task and
 * "No next step" rows link here).
 *
 * Opportunities are created from the account itself, not from the board
 * (Paul, Q7: the board's "Create opportunity" button is gone).
 *
 * Account (Paul, 10/21/26): a card's "Make Account" switches its Designation
 * to Account (tracked, not a lead), with Undo. Paul switches his own records
 * by hand (no suggestion list - Sharif, 10/21/26).
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Toast } from '../components/ui/Toast';
import LeadsKanban from '../components/crm/LeadsKanban';
import LeadsTable from '../components/crm/LeadsTable';
import AccountDetailPanel from '../components/crm/AccountDetailPanel';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canEdit, canDelete } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { TIERS } from '../data/crmStages';
import {
  BOARD_STATUSES,
  DEFAULT_FILTERS,
  buildLeadCards,
  filterCards,
  filterCounts,
  ownerOptions,
  verticalOptions,
} from '../components/crm/leadsBoard';
import { useTasksChanged } from '../components/tasks/useTasks';
import { latestReads } from '../live/useLive';

const VIEW_MODES = ['board', 'table'];

function parseHashAccount() {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx < 0) return null;
  return new URLSearchParams(hash.slice(qIdx + 1)).get('account') || null;
}

// Leaving the record drops ?account= from the address bar, so a reload (or a
// later data refresh) does not throw the user back into it.
function clearHashAccount() {
  try {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    if (!params.has('account')) return;
    params.delete('account');
    const rest = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${hash.slice(0, qIdx)}${rest ? `?${rest}` : ''}`
    );
  } catch {
    /* the link just stays in the address bar */
  }
}

/* Per-user, per-browser choice (Board | Table, Active | Cold). Storage can be
   blocked or empty - the page then simply opens on the default. */
function readPref(key, allowed, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}
function useUserPref(name, email, allowed, fallback) {
  const key = `globalops_leads_${name}:${String(email || '').toLowerCase()}`;
  const [value, setValue] = useState(() => readPref(key, allowed, fallback));
  useEffect(() => {
    setValue(readPref(key, allowed, fallback));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = useCallback(
    (v) => {
      setValue(v);
      try {
        window.localStorage.setItem(key, v);
      } catch {
        /* not remembered this time */
      }
    },
    [key]
  );
  return [value, set];
}

const LABEL = { fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase' };
// longhand border so a chip's colour can change without React dropping the shorthand
const CHIP = {
  padding: '4px 10px',
  borderRadius: 16,
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: '#E4E4E4',
  background: '#fff',
  color: '#666',
};
const CHIP_ON = { background: '#1B5E20', color: '#fff', borderColor: '#1B5E20' };

const SMALL_BTN = {
  padding: '3px 9px',
  borderRadius: 6,
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  border: '1px solid #CFD8DC',
  background: '#fff',
  color: '#455A64',
  whiteSpace: 'nowrap',
};

/** Two-way switch, styled like the Opportunities "My | All" toggle. */
function Toggle({ options, value, onChange, small }) {
  return (
    <div style={{ display: 'inline-flex', borderRadius: 8, border: '1px solid #D8DED4', overflow: 'hidden', flexShrink: 0 }}>
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-pressed={value === key}
          style={{
            padding: small ? '5px 12px' : '8px 16px',
            border: 'none',
            cursor: 'pointer',
            fontSize: small ? 10.5 : 11.5,
            fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif",
            background: value === key ? '#1B5E20' : '#fff',
            color: value === key ? '#fff' : '#555',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function LeadsView() {
  const { allAccounts, allProjects, updateAccount, trackRecentlyViewed } = useApp();
  const { user } = useAuth();
  const access = useMyAccess();
  const editable = canEdit(access, 'accounts');
  const deletable = canDelete(access, 'accounts');

  const [viewMode, setViewMode] = useUserPref('view', user?.email, VIEW_MODES, 'board');
  const [status, setStatus] = useUserPref('status', user?.email, BOARD_STATUSES, DEFAULT_FILTERS.status);
  const [picks, setPicks] = useState({
    owner: DEFAULT_FILTERS.owner,
    tier: DEFAULT_FILTERS.tier,
    vertical: DEFAULT_FILTERS.vertical,
    review: DEFAULT_FILTERS.review,
  });
  const setPick = (k, v) => setPicks((f) => ({ ...f, [k]: v }));
  const filters = useMemo(() => ({ ...picks, status }), [picks, status]);

  const [selected, setSelected] = useState(() => {
    const id = parseHashAccount();
    return id ? (allAccounts || []).find((a) => a.id === id) || null : null;
  });
  const [toast, setToast] = useState(null);
  const [undo, setUndo] = useState(null); // { id, name } - the last "Make Account", until undone or dismissed
  const [busyId, setBusyId] = useState(null);
  // open LEAD tasks (entity_type=account) feed each card's next-action line and
  // its "No next step" flag. null = not loaded yet (or the first read failed):
  // no flags then. A later read that fails keeps the last list - a 429 or 502
  // while every open tab re-reads must not strip every card of its next step.
  const [openActions, setOpenActions] = useState(null);
  const [actionReads] = useState(latestReads); // reads can overlap: the newest answer GOT wins

  // (re)load on arrival and every time the user comes back from a record, so
  // a task added or completed there shows on its card straight away - and
  // whenever a task changes anywhere, someone else's included (useTasksChanged
  // carries the live events, src/live)
  const onList = !selected;
  const [tasksTick, setTasksTick] = useState(0);
  const bumpTasks = useCallback(() => setTasksTick((t) => t + 1), []);
  useTasksChanged(bumpTasks);
  useEffect(() => {
    if (!onList) return;
    const ticket = actionReads.start();
    fetch('/api/crm-actions?status=open&entity_type=account')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (Array.isArray(d) && actionReads.show(ticket)) setOpenActions(d);
      })
      .catch(() => {
        /* keep the cards as they are */
      });
  }, [onList, tasksTick, actionReads]);

  useEffect(() => {
    if (selected) trackRecentlyViewed('account', selected.id, selected.name);
  }, [selected, trackRecentlyViewed]);

  // ?account=<id> while already on this page
  useEffect(() => {
    const onHash = () => {
      const id = parseHashAccount();
      if (!id) return;
      const acct = (allAccounts || []).find((a) => a.id === id);
      if (acct) setSelected(acct);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, [allAccounts]);

  // Cold-load deep link: accounts are not fetched yet on first render, so open
  // the record once they arrive (the ref stops a later refresh reopening it).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current || selected) return;
    const id = parseHashAccount();
    if (!id) return;
    const acct = (allAccounts || []).find((a) => a.id === id);
    if (acct) {
      setSelected(acct);
      deepLinkHandledRef.current = true;
    }
  }, [allAccounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = useMemo(
    () => buildLeadCards(allAccounts, { projects: allProjects, actions: openActions }),
    [allAccounts, allProjects, openActions]
  );
  const shown = useMemo(() => filterCards(cards, filters), [cards, filters]);
  const counts = useMemo(() => filterCounts(cards, filters), [cards, filters]);
  // chips come from every Active and Cold lead, so they hold still when the
  // toggle flips; a picked value stays listed even if its last lead moved on
  const owners = useMemo(() => ownerOptions(cards), [cards]);
  const verticals = useMemo(() => verticalOptions(cards), [cards]);
  const withPicked = (list, v) => (list.includes(v) ? list : [...list, v]);

  // L-09: a refused move (e.g. the Discovery Meeting Complete meeting gate)
  // shows the server's reason; the card stays where it was, because the
  // columns are drawn from the saved records and those did not change.
  const moveStage = async (card, stage) => {
    const res = await updateAccount(card.id, { stage });
    if (res && res.ok === false) setToast({ message: res.error || `Could not move ${card.name} - try again.`, tone: 'error' });
    else setToast(`${card.name} → ${stage}`);
  };
  const openCard = (c) => setSelected((allAccounts || []).find((a) => a.id === c.id) || c);

  // Account (Paul, 10/21/26): a PUT like any edit - the Activity log records it
  // and can revert it; Undo here puts the Designation back to Lead
  const save = async (id, fields, done) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const res = await updateAccount(id, fields);
      if (res && res.ok === false) setToast({ message: res.error || 'Could not save - try again.', tone: 'error' });
      else done();
    } finally {
      setBusyId(null);
    }
  };
  const makeAccount = (c) => save(c.id, { designation: 'Account' }, () => setUndo({ id: c.id, name: c.label || c.name }));
  const undoMakeAccount = () => {
    const u = undo;
    if (!u) return;
    save(u.id, { designation: 'Lead' }, () => {
      setUndo(null);
      setToast(`${u.name} is a Lead again`);
    });
  };

  if (selected) {
    // Re-derive from the store so edits made in the panel show immediately.
    const live = allAccounts.find((a) => a.id === selected.id) || selected;
    return (
      <AccountDetailPanel
        key={live.id}
        account={live}
        onBack={() => {
          clearHashAccount();
          setSelected(null);
        }}
        backLabel="Back to Leads"
        editable={editable}
        deletable={deletable}
        onOpenAccount={(a) => setSelected(a)}
      />
    );
  }

  const filtered = picks.owner !== 'All' || picks.tier !== 'All' || picks.vertical !== 'All' || picks.review;

  return (
    <div>
      {toast && <Toast message={toast.message || toast} tone={toast.tone} onDone={() => setToast(null)} />}

      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}
      >
        <div>
          <div className="text-eyebrow uppercase text-text-muted mb-1">Workspace · Leads Pipeline</div>
          <p className="text-body-sm text-text-body m-0">
            <b className="text-text-dark tabular-nums">{shown.length}</b> {status.toLowerCase()} {shown.length === 1 ? 'lead' : 'leads'}
            {filtered ? ' match the filters' : ''}
          </p>
        </div>
        <Toggle
          options={[
            ['board', 'Board'],
            ['table', 'Table'],
          ]}
          value={viewMode}
          onChange={setViewMode}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Toggle small value={status} onChange={setStatus} options={BOARD_STATUSES.map((s) => [s, `${s} ${counts[s]}`])} />
        <span style={{ ...LABEL, marginLeft: 10 }}>Owner</span>
        {withPicked(owners, picks.owner).map((o) => (
          <button key={o} type="button" onClick={() => setPick('owner', o)} style={{ ...CHIP, ...(picks.owner === o ? CHIP_ON : {}) }}>
            {o}
          </button>
        ))}
        <span style={{ ...LABEL, marginLeft: 10 }}>Tier</span>
        {['All', ...TIERS].map((t) => (
          <button key={t} type="button" onClick={() => setPick('tier', t)} style={{ ...CHIP, ...(picks.tier === t ? CHIP_ON : {}) }}>
            {t}
          </button>
        ))}
        {verticals.length > 1 && (
          <>
            <span style={{ ...LABEL, marginLeft: 10 }}>Vertical</span>
            {withPicked(verticals, picks.vertical).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setPick('vertical', v)}
                style={{ ...CHIP, ...(picks.vertical === v ? CHIP_ON : {}) }}
              >
                {v}
              </button>
            ))}
          </>
        )}
        {/* L-02: records the stage migration parked in Contacted / Nurturing for Paul's review */}
        {(counts.review > 0 || picks.review) && (
          <button
            type="button"
            onClick={() => setPick('review', !picks.review)}
            style={{
              ...CHIP,
              marginLeft: 10,
              ...(picks.review
                ? { background: '#E65100', color: '#fff', borderColor: '#E65100' }
                : { color: '#E65100', borderColor: '#FFCC80' }),
            }}
          >
            Needs review ({counts.review})
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#999' }}>
          {viewMode === 'table'
            ? 'Click a row to open the lead'
            : editable
              ? 'Drag a card to change its stage - the timestamp is written automatically'
              : 'Read-only view'}
        </span>
      </div>

      {undo && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#ECEFF1',
            border: '1px solid #CFD8DC',
            borderRadius: 8,
            padding: '7px 12px',
            marginBottom: 12,
            fontSize: 11,
            color: '#263238',
          }}
        >
          <span style={{ flex: 1 }}>
            <b>{undo.name}</b> is now an Account - off the Leads board and out of the lead counts.
          </span>
          <button type="button" onClick={undoMakeAccount} disabled={!!busyId} style={SMALL_BTN}>
            Undo
          </button>
          <button
            type="button"
            onClick={() => setUndo(null)}
            aria-label="Dismiss"
            style={{ ...SMALL_BTN, border: 'none', background: 'transparent' }}
          >
            x
          </button>
        </div>
      )}

      {viewMode === 'table' ? (
        <LeadsTable cards={shown} onOpen={openCard} fileName={`leads-${status.toLowerCase()}.csv`} />
      ) : (
        <LeadsKanban cards={shown} editable={editable} onOpen={openCard} onStageChange={moveStage} onMakeAccount={makeAccount} />
      )}
    </div>
  );
}
